class PointerDaysTheme {
  constructor() {
    this.body = document.body;
    this.liveRegion = document.querySelector('[data-live-region]');
    this.bindNavigation();
    this.bindCartDrawer();
    this.bindAjaxForms();
    this.bindVariantSelectors();
    this.bindAutoSubmit();
    this.bindPredictiveSearch();
    this.bindProductImageLightbox();
    this.loadRecommendations();
  }

  announce(message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = message;
  }

  bindNavigation() {
    document.querySelectorAll('[data-mobile-menu-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const menu = document.querySelector('[data-mobile-menu]');
        if (!menu) return;
        const isOpen = menu.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(isOpen));
      });
      const menu = document.querySelector('[data-mobile-menu]');
      const closeMenu = () => {
        menu?.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
      };
      menu?.querySelectorAll('a').forEach(link => link.addEventListener('click', closeMenu));
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') {
          closeMenu();
          button.focus();
        }
      });
    });

    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchPanel = document.querySelector('[data-search-panel]');
    if (searchToggle && searchPanel) {
      searchToggle.addEventListener('click', () => {
        const opening = searchPanel.hidden;
        searchPanel.hidden = !opening;
        searchToggle.setAttribute('aria-expanded', String(opening));
        if (opening) searchPanel.querySelector('input')?.focus();
      });
    }
  }

  bindCartDrawer() {
    document.addEventListener('click', (event) => {
      const openButton = event.target.closest('[data-cart-open]');
      const closeButton = event.target.closest('[data-cart-close]');
      if (openButton) {
        event.preventDefault();
        this.openCart();
      }
      if (closeButton) this.closeCart();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeCart();
    });
  }

  openCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    this.body.classList.add('drawer-open');
    drawer.querySelector('[data-cart-close]')?.focus();
  }

  closeCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || drawer.getAttribute('aria-hidden') === 'true') return;
    drawer.setAttribute('aria-hidden', 'true');
    this.body.classList.remove('drawer-open');
  }

  bindAjaxForms() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-ajax-product-form]');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const original = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding…';
      }
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This item could not be added.');
        await this.refreshCart();
        this.announce(`${result.product_title || 'Item'} added to your pack.`);
        this.openCart();
      } catch (error) {
        this.announce(error.message);
        const errorBox = form.querySelector('[data-product-error]');
        if (errorBox) errorBox.textContent = error.message;
      } finally {
        if (button) {
          button.disabled = false;
          button.textContent = original;
        }
      }
    });
  }

  async refreshCart() {
    const root = window.Shopify?.routes?.root || '/';
    const [sectionResponse, cartResponse] = await Promise.all([
      fetch(`${root}?section_id=cart-drawer`),
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    ]);
    if (sectionResponse.ok) {
      const html = await sectionResponse.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = documentFragment.querySelector('[data-cart-drawer]');
      const existing = document.querySelector('[data-cart-drawer]');
      if (replacement && existing) existing.replaceWith(replacement);
    }
    if (cartResponse.ok) {
      const cart = await cartResponse.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
    }
  }

  bindVariantSelectors() {
    document.querySelectorAll('[data-variant-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const form = select.closest('form');
        const button = form?.querySelector('[type="submit"]');
        const price = document.querySelector('[data-product-price]');
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (button) {
          const available = option.dataset.available === 'true';
          button.disabled = !available;
          button.textContent = available ? button.dataset.addLabel : button.dataset.soldLabel;
        }
        if (option.value && window.history?.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          window.history.replaceState({}, '', url);
        }
      });
    });
  }

  bindAutoSubmit() {
    document.querySelectorAll('[data-auto-submit]').forEach((control) => {
      control.addEventListener('change', () => control.closest('form')?.submit());
    });
  }

  bindPredictiveSearch() {
    const input = document.querySelector('[data-predictive-search-input]');
    const results = document.querySelector('[data-predictive-results]');
    if (!input || !results) return;
    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) {
        results.hidden = true;
        return;
      }
      timer = setTimeout(async () => {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&section_id=predictive-search`);
        if (!response.ok) return;
        const html = await response.text();
        results.innerHTML = html;
        results.hidden = false;
      }, 220);
    });
  }

  bindProductImageLightbox() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-product-image-zoom]');
      if (trigger) {
        const lightbox = trigger.closest('section')?.querySelector('[data-product-lightbox]');
        const image = lightbox?.querySelector('[data-product-lightbox-image]');
        if (!lightbox || !image) return;
        this.lightboxTrigger = trigger;
        image.src = trigger.dataset.zoomSrc;
        image.alt = trigger.dataset.zoomAlt || '';
        lightbox.hidden = false;
        this.body.classList.add('lightbox-open');
        lightbox.querySelector('[data-product-lightbox-close]:not([tabindex="-1"])')?.focus();
        return;
      }

      if (event.target.closest('[data-product-lightbox-close]')) this.closeProductImageLightbox();
    });

    document.addEventListener('keydown', (event) => {
      const lightbox = document.querySelector('[data-product-lightbox]:not([hidden])');
      if (!lightbox) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeProductImageLightbox();
      } else if (event.key === 'Tab') {
        event.preventDefault();
        lightbox.querySelector('[data-product-lightbox-close]:not([tabindex="-1"])')?.focus();
      }
    });
  }

  closeProductImageLightbox() {
    const lightbox = document.querySelector('[data-product-lightbox]:not([hidden])');
    if (!lightbox) return;
    lightbox.hidden = true;
    const image = lightbox.querySelector('[data-product-lightbox-image]');
    if (image) {
      image.removeAttribute('src');
      image.alt = '';
    }
    this.body.classList.remove('lightbox-open');
    this.lightboxTrigger?.focus();
    this.lightboxTrigger = null;
  }

  loadRecommendations() {
    document.querySelectorAll('[data-recommendations-url]').forEach(async (container) => {
      const response = await fetch(container.dataset.recommendationsUrl);
      if (!response.ok) return;
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('[data-recommendations-url]');
      if (replacement?.innerHTML.trim()) container.innerHTML = replacement.innerHTML;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new PointerDaysTheme());
